import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, InputNumber, Row, Select, Spin, Tooltip} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import moment from "moment";
import {openNotification} from "../utils/openNotification";
import {InfoCircleOutlined} from "@ant-design/icons";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const LocationAssignmentRuleDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [locationOptions, setLocationOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();
        getLocations();

        if(props.mode === 'Edit') {
            const fieldsValues: any[] = [];
            fieldsValues.push({name: 'id', value: data.id});
            fieldsValues.push({name: 'itemId', value: data.item ? data.item.id : null});
            fieldsValues.push({name: 'locationId', value: data.location ? data.location.id : null});
            fieldsValues.push({name: 'quantity', value: data.quantity});
            fieldsValues.push({name: 'validFrom', value: data.validFrom ? moment(data.validFrom) : null});
            fieldsValues.push({name: 'validTo', value: data.validTo ? moment(data.validTo) : null});
            fieldsValues.push({name: 'version', value: data.version});
            form.setFields(fieldsValues);
        } else if (data.item && data.item.id) {
            form.setFields([{name: 'itemId', value: data.item.id}]);
        }
    }, []);


    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .post('/resource/items/page', {
                    start: 0,
                    length: 1000,
                    direction: 'asc',
                    sorted_field: 'id',
                    query: {
                    }
                })
                .then(response => {

                    const items = Array.isArray(response.data.data) ? response.data.data : [];
                    setItemOptions(items);

                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error);
                    openNotification('error', 'Failed to load items');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        } else {
            setItemOptions([]);
        }
    };

    const getLocations = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/locations')
                .then(response => {
                    const locations = Array.isArray(response.data) ? response.data : [];
                    setLocationOptions(locations);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const handleEdit = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            axiosInstance.current
                .put(
                    '/resource/location-assignment-rules/' + encodeURIComponent(form.getFieldValue(['id'])) + '/from-dto',
                    {
                        id: form.getFieldValue(['id']),
                        location: { id: form.getFieldValue(['locationId']) },
                        item: { id: form.getFieldValue(['itemId']) },
                        quantity: form.getFieldValue(['quantity']) || null,
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    }
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully updated.');
                    if (props.onClose) props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleFinish = () => {
        if (axiosInstance.current != null) {
            setLoading(true);

            axiosInstance.current
                .post(
                    '/resource/location-assignment-rules/from-dto',
                    {
                        location: { id: form.getFieldValue(['locationId']) },
                        item: { id: form.getFieldValue(['itemId']) },
                        quantity: form.getFieldValue(['quantity']) || null,
                        validFrom: form.getFieldValue(['validFrom']) ? moment(form.getFieldValue(['validFrom'])).format('YYYY-MM-DD HH:mm') : null,
                        validTo: form.getFieldValue(['validTo']) ? moment(form.getFieldValue(['validTo'])).format('YYYY-MM-DD HH:mm') : null
                    }
                )
                .then(() => {
                    openNotification('customSuccess', 'Successfully created.');
                    if (props.onClose) props.onClose();
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const onReset = () => {
        form.resetFields();
    };

    return (
        <Form
            id="newLocationAssignmentRule"
            layout='vertical'
            form={form}
            onFinish={() => (props.mode === 'Edit' ? handleEdit() : handleFinish())}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24} style={{marginBottom: 20}}><Col span={12}><span style={{color: "red", fontStyle: "italic"}}>* Mandantory fields</span></Col></Row>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="locationId"
                            label={<Trans>Location name of the relevant commodity</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the location</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select location</Trans>}
                                optionFilterProp="children"
                            >
                                {locationOptions.map(loc => (
                                    <Select.Option key={loc.id} value={loc.id}>
                                        {loc.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="itemId"
                            label={<Trans>MSC P/N</Trans>}
                            rules={[{ required: true, message: <Trans>Please select the item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="quantity"
                            label={
                                <span>
                                    <Trans>Quantity of the relevant product shipped to MSC, from declared location</Trans>
                                    {' '}
                                    <Tooltip title={<Trans>Leave empty, if relevant commodity is coming from the same location</Trans>}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </span>
                            }
                        >
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="validFrom"
                            label={
                            <>
                                <Trans>Delivery Period to MSC - Start</Trans>
                                <Tooltip title={<Trans>Considering arrival of relevant products to MSC</Trans>}>
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </>
                        }
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="validTo"
                            label={
                                <span>
                                    <Trans>Delivery Period to MSC - End</Trans>
                                    {' '}
                                    <Tooltip title={<Trans>This field can be left empty.</Trans>}>
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </span>
                            }
                        >
                            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div style={{ marginTop: 12 }}>
                <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default LocationAssignmentRuleDrawer;
